<script setup lang="ts">
import { ref } from "vue";
import { useTranslation } from "i18next-vue";
import DevInfoPopover from "./DevInfoPopover.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

/**
 * Outil de DÉVELOPPEMENT : un bouton de suppression en masse (toutes les
 * observations, toutes les parties d'un ouvrage) rangé dans l'infobulle
 * orange « Mode développeur », pour repartir d'un ouvrage vide pendant la
 * mise au point. Confirmation obligatoire. N'existe que dans une version de
 * développement : il n'est jamais livré aux inspecteurs.
 */

const props = defineProps<{
  /** Libellé du bouton. */
  label: string;
  /** Texte de la confirmation : ce qui va être supprimé. */
  confirmMessage: string;
  /** Rien à supprimer. */
  disabled?: boolean;
  /** La suppression elle-même. */
  action: () => Promise<void>;
}>();

const { t } = useTranslation();

const isDev = import.meta.env.DEV;

const showConfirm = ref(false);
const isRunning = ref(false);

const run = async (): Promise<void> => {
  isRunning.value = true;
  try {
    await props.action();
    showConfirm.value = false;
  } finally {
    isRunning.value = false;
  }
};
</script>

<template>
  <template v-if="isDev">
    <DevInfoPopover>
      <DRBtn
        icon="bi-trash"
        :label="label"
        variant="danger"
        size="xs"
        class="w-100"
        :disabled="disabled"
        @click="showConfirm = true"
      />
    </DevInfoPopover>

    <ConfirmationModal
      class="canvas-dialog"
      v-model="showConfirm"
      :title="label"
      :message="confirmMessage"
      :confirmLabel="t('delete')"
      :loading="isRunning"
      danger
      @confirm="run"
    />
  </template>
</template>
